import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { GenericForm, GenericFormField } from '@/styles/GenericForm';
import { GenericAttachmentsSection } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { ItemSelectionMenu, hoverAsCancel } from '@/styles/ItemSelectionMenu';
import { uploadChecklistAttachments, deleteChecklistAttachment } from '@/features/project-management/checklists/store';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { useEntityComments } from '@/hooks/useEntityComments';
import { getApiUrl } from '@/lib/api-config';
import { cn } from '@/lib/utils';

interface FileAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  created_at: string;
  created_by: string;
  created_by_user?: {
    display_name?: string | null;
  };
}

interface Project {
  id: string;
  name: string;
}

interface User {
  id: string;
  full_name: string;
  company_name?: string | null;
  email?: string;
}


interface Checklist {
  id: string;
  title: string;
  description: string;
  project_id: string;
  assignee_id: string;
  status: string;
  priority: string;
  due_date: string;
  due_time: string;
  reminder_times: string | null;
  checklist_items?: Array<{
    id: string;
    text: string;
    completed: boolean;
    item_order: number;
  }>;
}

interface ChecklistFormProps {
  mode: 'create' | 'edit';
  initialData?: Checklist;
  projects: Project[];
  users: User[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
  attachments?: FileAttachment[];
  onDeleteAttachment?: (id: string) => void;
}

export function ChecklistForm({
  mode,
  initialData,
  projects,
  users,
  onSubmit,
  onCancel,
  attachments = [],
  onDeleteAttachment
}: ChecklistFormProps) {
  const { companyId, user } = useUser();
  const { selectedProject } = useProject();
  const uploadChecklistAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<FileAttachment[]> => {
    if (!companyId || !user?.id) return [];
    return uploadChecklistAttachments(id, files, companyId, user.id);
  }, [companyId, user]);
  const deleteChecklistAttachmentFn = useCallback((id: string) => deleteChecklistAttachment(id, user?.id), [user]);
  const {
    pendingFiles: selectedFiles,
    uploadedFiles,
    setUploadedFiles,
    handleUpload: handleFileUpload,
    handleRemovePending: handleRemoveSelectedFile,
    handleDelete: handleDeleteFileBase,
  } = useAttachmentStaging<FileAttachment>({
    entityId: initialData?.id,
    uploadFn: uploadChecklistAttachmentsFn,
    deleteFn: deleteChecklistAttachmentFn,
    initialUploadedFiles: attachments,
  });
  const handleDeleteFile = useCallback(async (attachmentId: string) => {
    // GenericAttachmentsSection already confirms deletion itself -- a second
    // confirm() here was firing the warning twice (unlike To-Do's wrapper,
    // which has none of its own).
    const ok = await handleDeleteFileBase(attachmentId);
    if (ok) {
      onDeleteAttachment?.(attachmentId);
    }
  }, [handleDeleteFileBase, onDeleteAttachment]);
  // Comments: the shared hook (API reads and writes; the form confirms deletes through the generic section).
  const { comments, addComment, deleteComment } = useEntityComments('checklist', mode === 'edit' ? initialData?.id : null);

  // Checklist items state
  const [checklistItems, setChecklistItems] = useState<Array<{id: string, text: string, completed: boolean, item_order: number}>>([]);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  // Snapshot of the items this checklist opened with -- used both to know
  // which item ids already exist in the DB (so toggling them can autosave,
  // see toggleChecklistItem) and to diff against for the unsaved-changes
  // check below (see itemsHaveUnsavedChanges).
  const checklistItemsInitialRef = useRef<Array<{id: string, text: string, completed: boolean, item_order: number}>>(
    initialData?.checklist_items ? initialData.checklist_items.map(item => ({ ...item })) : []
  );

  const normalizeDueTime = (t: string | null | undefined) => {
    if (!t) return null;
    return t.slice(0, 5);
  };

  const [dueDate, setDueDate] = useState<string | null>(initialData?.due_date || null);
  const [dueTime, setDueTime] = useState<string | null>(normalizeDueTime(initialData?.due_time) || null);

  const timeOptions = useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = new Date();
        time.setHours(hour, minute, 0);
        const formattedTime = time.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        options.push({
          label: formattedTime,
          value: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        });
      }
    }
    return options;
  }, []);

  const initialValues = useMemo(() => ({
    title: initialData?.title || '',
    description: initialData?.description || '',
    project_id: initialData?.project_id || getDefaultProjectIdForNewRecord(selectedProject),
    assignee_id: initialData?.assignee_id || '',
    status: initialData?.status || 'pending',
    priority: initialData?.priority || 'medium',
    due_date: initialData?.due_date || '',
    due_time: normalizeDueTime(initialData?.due_time) || '',
  }), [initialData, selectedProject]);

  // Live form data + a one-time snapshot of initialValues, mirroring the
  // hasUnsavedChanges pattern already used by WarrantyForm/UsersTab/
  // ProjectForm -- reused here instead of inventing a new mechanism.
  const [formData, setFormData] = useState<Record<string, any>>({});
  const initialValuesRef = useRef<Record<string, any>>({});
  const previousIsOpenRef = useRef<boolean>(false);

  useEffect(() => {
    if (!previousIsOpenRef.current) {
      const deepCopy = JSON.parse(JSON.stringify(initialValues));
      initialValuesRef.current = deepCopy;
      setFormData(deepCopy);
    }
    previousIsOpenRef.current = true;
  }, [initialValues]);

  const checklistFields = useMemo<GenericFormField[]>(() => [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      row: 1,
      placeholder: 'Enter Checklist Title'
    },
    {
      name: 'project_id',
      label: 'Project',
      type: 'select',
      required: true,
      row: 1,
      options: projects.map(p => ({ label: p.name, value: p.id }))
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      row: 2,
      colSpan: 2,
      placeholder: 'Enter checklist description'
    },
    {
      name: 'assignee_id',
      label: 'Assignee',
      type: 'select',
      row: 3,
      placeholder: 'Select',
      options: users.map(u => ({ label: u.company_name || u.full_name || u.email || '', value: u.id })),
      membershipProjectField: 'project_id'
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      hideRequiredMark: true,
      row: 3,
      options: [
        { label: 'Pending', value: 'pending' },
        { label: 'In Progress', value: 'in_progress' },
        { label: 'Completed', value: 'completed' }
      ]
    },
    {
      name: 'due_date',
      label: 'Due Date',
      type: 'date',
      row: 4,
      colSpan: 0.5
    },
    {
      name: 'due_time',
      label: 'Due Time',
      type: 'select',
      row: 4,
      colSpan: 0.5,
      placeholder: 'Select',
      disabled: !dueDate,
      options: timeOptions
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'select',
      required: true,
      hideRequiredMark: true,
      row: 4,
      colSpan: 0.5,
      options: [
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' }
      ]
    }
  ], [projects, users, timeOptions, dueDate]);

  const handleFieldChange = (name: string, value: any, currentFormData: Record<string, any>) => {
    setFormData(currentFormData);
    if (name === 'due_date') {
      const cleared = !value;
      setDueDate(cleared ? null : value);
      if (cleared) {
        // Due Time can't exist without a Due Date -- clearing the date
        // clears whatever time was set, matching TodoForm's same rule.
        setDueTime(null);
      }
    } else if (name === 'due_time') {
      setDueTime(value || null);
    }
  };

  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (data: Record<string, any>) => {
    // Title / Project are `required` fields -- GenericForm blocks the submit
    // itself (generic required validation), nothing to re-check here.
    if (data.due_time && !data.due_date) {
      setValidationError('Please select a due date before setting a due time.');
      return;
    }
    setValidationError(null);

    onSubmit({
      ...data,
      title: data.title.trim(),
      description: data.description.trim(),
      project_id: data.project_id,
      assignee_id: data.assignee_id || null,
      status: data.status,
      priority: data.priority,
      due_date: data.due_date || null,
      due_time: data.due_time || null,
      checklist_items: checklistItems,
      attachmentsFiles: selectedFiles
    });
  };


  // Checklist items handlers
  const addChecklistItem = () => {
    const newItem = {
      id: Date.now().toString(),
      text: '',
      completed: false,
      item_order: checklistItems.length + 1
    };
    setChecklistItems([...checklistItems, newItem]);
    
    setTimeout(() => {
      const inputs = document.querySelectorAll('input[placeholder="Enter checklist item..."]');
      const lastInput = inputs[inputs.length - 1] as HTMLInputElement;
      if (lastInput) {
        lastInput.focus();
      }
    }, 0);
  };

  const removeChecklistItem = (id: string) => {
    if (!window.confirm('Are you sure you want to delete this checklist item?')) {
      return;
    }
    
    const updatedItems = checklistItems.filter(item => item.id !== id);
    const reorderedItems = updatedItems.map((item, index) => ({
      ...item,
      item_order: index + 1
    }));
    setChecklistItems(reorderedItems);
    setSelectedItems(selectedItems.filter(itemId => itemId !== id));
  };

  const removeSelectedItems = () => {
    if (selectedItems.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} selected item(s)?`)) {
      return;
    }

    const updatedItems = checklistItems.filter(item => !selectedItems.includes(item.id));
    const reorderedItems = updatedItems.map((item, index) => ({
      ...item,
      item_order: index + 1
    }));
    setChecklistItems(reorderedItems);
    setSelectedItems([]);
    setIsSelectMode(false);
  };

  // Copies the selected items within this same checklist -- appended at
  // the end, unchecked, with fresh ids/order (same text, same shape as a
  // brand-new item so nothing downstream needs to special-case a copy).
  const copySelectedItems = () => {
    if (selectedItems.length === 0) return;
    const selectedInOrder = checklistItems.filter(item => selectedItems.includes(item.id));
    const copies = selectedInOrder.map((item, index) => ({
      id: `${Date.now().toString()}-${index}`,
      text: item.text,
      completed: false,
      item_order: checklistItems.length + index + 1,
    }));
    setChecklistItems([...checklistItems, ...copies]);
    setSelectedItems([]);
  };

  const updateChecklistItem = (id: string, text: string) => {
    setChecklistItems(checklistItems.map(item => 
      item.id === id ? { ...item, text } : item
    ));
  };

  // Item ids present when this checklist opened already exist in
  // `checklist_items` -- toggling one of those can be persisted right away.
  // A brand-new item (added via addChecklistItem/copySelectedItems) has no
  // DB row yet, so its completed state just stays local until Save Changes
  // creates it (point 8).
  const togglingItemIdsRef = useRef<Set<string>>(new Set());

  const toggleChecklistItem = async (id: string) => {
    if (togglingItemIdsRef.current.has(id)) return;

    const item = checklistItems.find(i => i.id === id);
    if (!item) return;
    const nextCompleted = !item.completed;

    setChecklistItems(prev => prev.map(i =>
      i.id === id ? { ...i, completed: nextCompleted } : i
    ));

    const isPersisted = checklistItemsInitialRef.current.some(i => i.id === id);
    if (!isPersisted || !companyId || !user?.id) return;

    togglingItemIdsRef.current.add(id);
    try {
      const response = await fetch(getApiUrl(`api/entities/checklist_item/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          company_id: companyId,
          completed: nextCompleted,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update checklist item');
      }
    } catch (error) {
      console.error('[Checklist] toggle item failed', error);
      // Revert the optimistic update -- this specific toggle failed to
      // persist, so the UI shouldn't claim it's completed/incomplete.
      setChecklistItems(prev => prev.map(i =>
        i.id === id ? { ...i, completed: !nextCompleted } : i
      ));
      setValidationError('Failed to update checklist item. Please try again.');
    } finally {
      togglingItemIdsRef.current.delete(id);
    }
  };

  const enterSelectMode = () => {
    setIsSelectMode(true);
    setSelectedItems([]);
  };

  const exitSelectMode = () => {
    setIsSelectMode(false);
    setSelectedItems([]);
  };

  const toggleItemSelection = (id: string) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  useEffect(() => {
    if (initialData?.id) {
      setUploadedFiles(attachments || []);
      
      if (initialData.checklist_items && Array.isArray(initialData.checklist_items)) {
        setChecklistItems(initialData.checklist_items);
      }
    }
  }, [initialData?.id, attachments, initialData?.checklist_items, setUploadedFiles]);


  // Same field-level comparison every other GenericForm consumer with a
  // custom hasUnsavedChanges uses (WarrantyForm, UsersTab, ProjectForm).
  const hasFormChanged = (current: Record<string, any>, initial: Record<string, any>): boolean => {
    const allKeys = Array.from(new Set([...Object.keys(current), ...Object.keys(initial)]));
    for (const key of allKeys) {
      const currentStr = current[key] == null ? '' : String(current[key]).trim();
      const initialStr = initial[key] == null ? '' : String(initial[key]).trim();
      if (currentStr !== initialStr) return true;
    }
    return false;
  };

  // Diffs checklistItems against the snapshot taken on open. `completed` is
  // deliberately excluded: that field autosaves immediately (point 7), so a
  // toggle must never surface as a pending change (point 8) -- only
  // create/copy/delete (a different id set, or different text/order) count.
  const itemsHaveUnsavedChanges = (): boolean => {
    const current = checklistItems;
    const initial = checklistItemsInitialRef.current;
    if (current.length !== initial.length) return true;
    const initialById = new Map(initial.map(item => [item.id, item]));
    for (const item of current) {
      const original = initialById.get(item.id);
      if (!original) return true;
      if (item.text !== original.text || item.item_order !== original.item_order) return true;
    }
    return false;
  };

  const hasUnsavedChanges = () => {
    if (hasFormChanged(formData, initialValuesRef.current)) return true;
    if (selectedFiles.length > 0) return true;
    return itemsHaveUnsavedChanges();
  };

  const checklistItemsSection = (
    <div className="space-y-2">
            <div className="flex items-center justify-between">
        <Label className="font-form-label">Checklist Items</Label>
              <div className="flex items-center gap-2">
                <ItemSelectionMenu
                  isSelectMode={isSelectMode}
                  hasSelection={selectedItems.length > 0}
                  onEnterSelectMode={enterSelectMode}
                  onExitSelectMode={exitSelectMode}
                  onCopy={copySelectedItems}
                  onDelete={removeSelectedItems}
                >
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addChecklistItem}
                    className="h-8 px-3 bg-white border-gray-300"
                    {...hoverAsCancel}
                  >
                    + Add Item
                  </Button>
                </ItemSelectionMenu>
              </div>
            </div>
            <div className="space-y-0">
              {checklistItems.map((item, index) => (
                <div key={item.id} className={cn(
                  "flex items-center gap-2 border rounded-md",
                  isSelectMode ? "p-1.5" : "p-2",
                  index > 0 && "rounded-t-none border-t-0"
                )}>
                  {isSelectMode && (
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => toggleItemSelection(item.id)}
                      className="h-4 w-4"
                    />
                  )}
                  <span className="text-sm text-gray-500 w-6 flex-shrink-0">{item.item_order}.</span>
                  <span
                    role="button"
                    tabIndex={isSelectMode ? -1 : 0}
                    onClick={() => !isSelectMode && toggleChecklistItem(item.id)}
                    onKeyDown={(e) => {
                      if (!isSelectMode && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        toggleChecklistItem(item.id);
                      }
                    }}
                    aria-label={item.completed ? 'Mark as not complete' : 'Mark as complete'}
                    // A <button> would be forced back to rounded-lg by
                    // index.css's global `button { border-radius !important }`
                    // rule -- rendered as a <span> instead so `rounded-full`
                    // actually applies, no override needed to fight it.
                    className={cn(
                      "h-5 w-5 rounded-full border-2 flex-shrink-0 transition-colors inline-block",
                      item.completed ? "bg-green-500 border-green-500" : "border-gray-300 bg-white",
                      isSelectMode ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                    )}
                  />
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => updateChecklistItem(item.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !item.completed) {
                        e.preventDefault();
                        addChecklistItem();
                      }
                    }}
                    placeholder="Enter checklist item..."
                    disabled={item.completed}
                    className={cn(
                      "flex-1 px-2 py-1 text-sm border rounded",
                      item.completed && "line-through text-gray-500 bg-gray-50 cursor-not-allowed"
                    )}
                  />
                </div>
              ))}
              {checklistItems.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-4">
                  No checklist items yet. Click "Add Item" to get started.
                </div>
              )}
            </div>
          </div>
  );

  // Browse Drive follows the form's own Project field
  const attachmentsSection = (projectId: string) => (
    <GenericAttachmentsSection
      projectId={projectId}
      attachments={uploadedFiles.map(file => ({
        id: file.id,
        file_name: file.file_name,
        file_type: file.file_type,
        file_size: file.file_size,
        file_url: file.file_url,
        created_at: file.created_at,
        created_by: file.created_by,
        created_by_user: file.created_by_user
      }))}
      pendingFiles={selectedFiles}
      onUpload={handleFileUpload}
      onDelete={handleDeleteFile}
      onRemovePending={handleRemoveSelectedFile}
      mode={mode}
    />
  );

  const commentsSection = (
    <GenericCommentsSection
      comments={comments}
      onAddComment={addComment}
      onDeleteComment={deleteComment}
      mode={mode}
      audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
                placeholder={mode === 'create' ? "Comments are available after creating the checklist" : "Add a comment..."}
    />
  );

  return (
    <GenericForm
      title="Checklist"
      mode={mode}
      fields={checklistFields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isOpen={true}
      onFieldChange={handleFieldChange}
      error={validationError}
      additionalSections={(values) => (
        <>
          {checklistItemsSection}
          {attachmentsSection(values.project_id || '')}
          {commentsSection}
        </>
      )}
      submitButtonText="Save Changes"
      hasUnsavedChanges={hasUnsavedChanges}
    />
  );
}
